
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LucideIcon, Upload, X } from "lucide-react";

interface MenuItemType {
  icon: LucideIcon;
  label: string;
  path: string;
}

interface MobileMenuDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItems: MenuItemType[];
  onNavigate: (path: string) => void;
  organizationId?: string;
  handleFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  isUploading?: boolean;
}

export const MobileMenuDrawer = ({
  open,
  onOpenChange,
  menuItems,
  onNavigate,
  organizationId,
  handleFileUpload,
  isUploading,
}: MobileMenuDrawerProps) => {
  const location = useLocation();
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85vh] max-h-[85vh] flex flex-col px-0 rounded-t-3xl">
        <DrawerHeader className="border-b px-6 py-3 flex justify-between items-center">
          <DrawerTitle className="text-lg font-semibold text-[#1A1F2C]">Menu</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-4">
            {menuItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-4 h-12 rounded-xl",
                  "text-[16px] font-medium",
                  location.pathname === item.path
                    ? "bg-[#9b87f5] text-white hover:bg-[#8E69DF]"
                    : "text-[#1A1F2C] hover:bg-[#F5F5F7]"
                )}
                onClick={() => onNavigate(item.path)}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
              </Button>
            ))}

            {/* Upload Statement Button */}
            {organizationId && handleFileUpload && (
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-4 h-12 rounded-xl",
                  "text-[16px] font-medium",
                  "text-[#1A1F2C] hover:bg-[#F5F5F7]"
                )}
                disabled={isUploading}
              >
                <Upload className="h-5 w-5 flex-shrink-0" />
                <label className="flex-1 text-left cursor-pointer">
                  {isUploading ? "Uploading..." : "Upload Statement"}
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      handleFileUpload(e);
                      // Reset the file input after upload
                      setFileInputKey(Date.now());
                    }}
                    disabled={isUploading}
                    key={fileInputKey}
                  />
                </label>
              </Button>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
